// src/character/creation/index.js

import { createRace, AVAILABLE_RACES } from '../../../game-system/races';
import { calculateAttributeModifier } from '../../../game-system/core/types/attributes';
import { rollDice } from '../../../game-system/core/utils/dice.js';
import { BACKGROUNDS, applyBackgroundBonuses } from '../../../game-system/character/backgrounds';

/**
 * @typedef {Object} CharacterCreationState
 * @property {string} step - Current creation step
 * @property {Object} character - Character data
 * @property {string[]} errors - Validation errors
 * @property {boolean} isValid - Overall validation state
 */

/**
 * Character creation steps in order
 * @type {Array<{id: string, label: string, required: boolean}>}
 */
export const CREATION_STEPS = [
  {
    id: 'basics',
    label: 'Basic Information',
    required: true,
    fields: ['name', 'race', 'subrace']
  },
  {
    id: 'attributes',
    label: 'Attributes',
    required: true,
    fields: ['attributes.strength', 'attributes.dexterity', 'attributes.constitution',
             'attributes.intelligence', 'attributes.wisdom', 'attributes.charisma']
  },
  {
    id: 'background',
    label: 'Background & Story',
    required: true,
    fields: ['background', 'alignment', 'personality', 'bonds', 'ideals', 'flaws']
  },
  {
    id: 'skills',
    label: 'Skills & Abilities',
    required: true,
    fields: ['skillPoints']
  },
  {
    id: 'equipment',
    label: 'Starting Equipment',
    required: true,
    fields: ['equipment', 'gold']
  },
  {
    id: 'review',
    label: 'Review & Finish',
    required: false,
    fields: []
  }
];

/**
 * Initial state for character creation
 * @type {Object}
 */
export const INITIAL_CHARACTER_STATE = {
  // Basic Information
  name: '',
  race: '',
  subrace: '',

  // Attributes (formerly ability scores)
  attributes: {
    strength: 10,
    dexterity: 10,
    constitution: 10,
    intelligence: 10,
    wisdom: 10,
    charisma: 10
  },

  // Skill System (replaces class)
  // Each skill has a rank from 0-5 (0=untrained, 5=legendary)
  skillPoints: 10, // Starting skill points to allocate
  skills: {
    // Combat Skills
    combat: {
      oneHandedWeapons: 0,  // Strength-based
      twoHandedWeapons: 0,  // Strength-based
      archery: 0,           // Dexterity-based
      thrown: 0,            // Dexterity-based
      defense: 0,           // Constitution-based
      unarmedCombat: 0      // Strength-based
    },

    // Magic Skills
    magic: {
      elementalMagic: 0,    // Intelligence-based
      healingMagic: 0,      // Wisdom-based
      divination: 0,        // Wisdom-based
      enchantment: 0,       // Charisma-based
      necromancy: 0         // Intelligence-based
    },

    // Social Skills
    social: {
      persuasion: 0,        // Charisma-based
      intimidation: 0,      // Strength/Charisma-based
      deception: 0,         // Charisma-based
      diplomacy: 0,         // Wisdom/Charisma-based
      trading: 0            // Intelligence/Charisma-based
    },

    // Survival Skills
    survival: {
      tracking: 0,          // Wisdom-based
      trapping: 0,          // Dexterity-based
      foraging: 0,          // Wisdom-based
      navigation: 0,        // Intelligence-based
      stealth: 0            // Dexterity-based
    },

    // Knowledge Skills
    knowledge: {
      history: 0,           // Intelligence-based
      arcana: 0,            // Intelligence-based
      nature: 0,            // Intelligence/Wisdom-based
      religion: 0,          // Intelligence/Wisdom-based
      engineering: 0        // Intelligence-based
    },

    // Crafting Skills
    crafting: {
      alchemy: 0,           // Intelligence-based
      smithing: 0,          // Strength-based
      woodworking: 0,       // Dexterity-based
      leatherworking: 0,    // Dexterity-based
      cooking: 0            // Constitution/Wisdom-based
    }
  },

  // Background Information
  background: '',           // Provides starting skill bonuses
  alignment: '',            // Moral compass
  personality: '',
  bonds: '',
  ideals: '',
  flaws: '',

  // Equipment and Resources
  equipment: [],
  gold: 50,

  // Derived Statistics
  level: 1,
  experience: 0,
  maxHealth: 0,           // Based on constitution and skills
  currentHealth: 0,
  armorClass: 10,         // Based on dexterity and defense skill
  initiative: 0,          // Based on dexterity
  speed: 30,              // Base speed, modified by race

  // Progress Tracking
  completedSteps: [],
  currentStep: 'basics'
};

/**
 * Generate attribute scores using various methods
 * @param {string} method - Method to use ('standard', 'roll', 'pointbuy')
 * @returns {Object.<string, number>}
 */
export function generateAttributes(method = 'standard') {
  switch (method) {
    case 'roll':
      return {
        strength: rollAttributeScore(),
        dexterity: rollAttributeScore(),
        constitution: rollAttributeScore(),
        intelligence: rollAttributeScore(),
        wisdom: rollAttributeScore(),
        charisma: rollAttributeScore()
      };

    case 'pointbuy':
      return {
        strength: 8,
        dexterity: 8,
        constitution: 8,
        intelligence: 8,
        wisdom: 8,
        charisma: 8
      };

    case 'standard':
    default:
      return {
        strength: 15,
        dexterity: 14,
        constitution: 13,
        intelligence: 12,
        wisdom: 10,
        charisma: 8
      };
  }
}

/**
 * Roll 4d6, drop lowest for attribute score
 * @returns {number}
 */
function rollAttributeScore() {
  const rolls = Array(4).fill(0).map(() => rollDice(6));
  rolls.sort((a, b) => b - a);
  return rolls.slice(0, 3).reduce((sum, roll) => sum + roll, 0);
}

/**
 * Get skill cost at a given rank
 * Higher ranks cost more points
 * @param {number} rank - Current rank of the skill
 * @returns {number} Cost to increase to next rank
 */
export function getSkillUpgradeCost(rank) {
  const costs = [1, 1, 2, 3, 5]; // Cost to go from rank N to N+1
  return rank >= 0 && rank < costs.length ? costs[rank] : -1;
}

/**
 * Calculate total spent skill points
 * @param {Object} skills - Character skills object
 * @returns {number} Total points spent
 */
export function calculateSpentSkillPoints(skills) {
  let total = 0;

  // For each skill category
  Object.values(skills).forEach(category => {
    // For each skill in category
    Object.values(category).forEach(rank => {
      // Add up costs for each rank
      for (let i = 0; i < rank; i++) {
        total += getSkillUpgradeCost(i);
      }
    });
  });

  return total;
}

/**
 * Calculate derived statistics based on character data
 * @param {Object} character - Character data
 * @returns {Object} Calculated statistics
 */
export function calculateDerivedStats(character) {
  const raceInstance = createRace(character.race, character.subrace);

  // Calculate attribute modifiers with racial bonuses
  const attributeModifiers = {};
  Object.entries(character.attributes).forEach(([attribute, score]) => {
    const racialBonus = raceInstance.getAttributeBonus(attribute) || 0;
    attributeModifiers[attribute] = calculateAttributeModifier(score + racialBonus);
  });

  // Health calculation now based on constitution and skills
  const constitutionMod = attributeModifiers.constitution || 0;
  const defenseSkill = character.skills.combat.defense || 0;
  const baseHealth = 10; // Base health for everyone

  // Calculate armor class based on dexterity and defense skill
  const dexterityMod = attributeModifiers.dexterity || 0;
  const armorBonus = Math.floor(defenseSkill / 2); // Each 2 points of defense adds 1 to AC

  // Calculate initiative from dexterity and relevant skills
  const stealthSkill = character.skills.survival.stealth || 0;
  const initiativeBonus = Math.floor(stealthSkill / 3); // Minor bonus from stealth

  return {
    maxHealth: baseHealth + (constitutionMod * 2) + defenseSkill,
    currentHealth: baseHealth + (constitutionMod * 2) + defenseSkill,
    armorClass: 10 + dexterityMod + armorBonus,
    initiative: dexterityMod + initiativeBonus,
    speed: raceInstance.speed,
    attributeModifiers
  };
}

/**
 * Generate a random name based on race and subrace
 * @param {string} race - Character race
 * @param {string} subrace - Character subrace
 * @returns {Promise<string>} Generated name
 */
export async function generateCharacterName(race, subrace) {
  try {
    const response = await fetch('/api/generate-name', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ race, subrace })
    });
    const data = await response.json();
    return data.name;
  } catch (error) {
    console.error('Error generating name:', error);
    return '';
  }
}

/**
 * Validate character data for a specific step
 * @param {Object} character - Character data
 * @param {string} step - Creation step to validate
 * @returns {Object} Validation result
 */
export function validateCreationStep(character, step) {
  const stepConfig = CREATION_STEPS.find(s => s.id === step);
  if (!stepConfig) return { isValid: false, errors: ['Invalid step'] };

  const errors = [];

  // Check if fields exist (works with nested paths like 'attributes.strength')
  stepConfig.fields.forEach(field => {
    if (stepConfig.required) {
      // Handle nested paths
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        if (!character[parent] || character[parent][child] === undefined) {
          errors.push(`${field} is required`);
        }
      } else if (!character[field]) {
        errors.push(`${field} is required`);
      }
    }
  });

  // Additional step-specific validation
  switch (step) {
    case 'attributes':
      Object.entries(character.attributes).forEach(([attribute, score]) => {
        if (score < 3 || score > 18) {
          errors.push(`${attribute} must be between 3 and 18`);
        }
      });
      break;

    case 'race':
      if (character.race && !AVAILABLE_RACES[character.race]) {
        errors.push('Invalid race selected');
      }
      break;

    case 'background':
      if (character.background && !BACKGROUNDS[character.background.toLowerCase()]) {
        errors.push('Invalid background selected');
      }
      break;

    case 'skills':
      // Make sure skill points are allocated correctly
      const spentPoints = calculateSpentSkillPoints(character.skills);
      if (spentPoints > character.skillPoints) {
        errors.push(`You have spent ${spentPoints} skill points, but only have ${character.skillPoints} available`);
      }

      // Check that no skill exceeds the maximum rank (5)
      Object.entries(character.skills).forEach(([category, skills]) => {
        Object.entries(skills).forEach(([skill, rank]) => {
          if (rank > 5) {
            errors.push(`${skill} cannot exceed rank 5`);
          }
        });
      });
      break;
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Get available options for a specific step
 * @param {string} step - Creation step
 * @param {Object} character - Current character data
 * @returns {Object} Available options
 */
export function getStepOptions(step, character = {}) {
  switch (step) {
    case 'race':
      return {
        races: Object.values(AVAILABLE_RACES),
        subraces: character.race ? AVAILABLE_RACES[character.race].availableSubraces : []
      };

    case 'background':
      return {
        backgrounds: Object.values(BACKGROUNDS).map(bg => bg.name),
        alignments: [
          'Lawful Good', 'Neutral Good', 'Chaotic Good',
          'Lawful Neutral', 'True Neutral', 'Chaotic Neutral',
          'Lawful Evil', 'Neutral Evil', 'Chaotic Evil'
        ]
      };

    case 'skills':
      // Get remaining skill points
      const spentPoints = calculateSpentSkillPoints(character.skills);
      const remainingPoints = character.skillPoints - spentPoints;

      // Get skill categories and their associated attributes
      const skillInfo = {};
      Object.entries(character.skills).forEach(([category, skills]) => {
        skillInfo[category] = {};
        Object.keys(skills).forEach(skillName => {
          const associatedAttribute = getSkillAttribute(category, skillName);
          const attributeModifier = character.attributes[associatedAttribute]
            ? Math.floor((character.attributes[associatedAttribute] - 10) / 2)
            : 0;

          skillInfo[category][skillName] = {
            rank: character.skills[category][skillName],
            attribute: associatedAttribute,
            attributeModifier: attributeModifier,
            upgradeCost: getSkillUpgradeCost(character.skills[category][skillName])
          };
        });
      });

      return {
        skillInfo,
        remainingPoints,
        maxRank: 5
      };

    case 'attributes':
      return {
        attributeMethods: [
          { id: 'standard', name: 'Standard Array', description: 'Use the standard set: 15, 14, 13, 12, 10, 8' },
          { id: 'roll', name: 'Roll Dice', description: 'Roll 4d6 drop lowest for each attribute' },
          { id: 'pointbuy', name: 'Point Buy', description: 'Spend points to build your attributes' }
        ]
      };

    default:
      return {};
  }
}

/**
 * Get the primary attribute associated with a skill
 * @param {string} category - Skill category
 * @param {string} skillName - Skill name
 * @returns {string} Associated attribute name
 */
export function getSkillAttribute(category, skillName) {
  // This is a simplified mapping
  const attributeMap = {
    combat: {
      oneHandedWeapons: 'strength',
      twoHandedWeapons: 'strength',
      archery: 'dexterity',
      thrown: 'dexterity',
      defense: 'constitution',
      unarmedCombat: 'strength'
    },
    magic: {
      elementalMagic: 'intelligence',
      healingMagic: 'wisdom',
      divination: 'wisdom',
      enchantment: 'charisma',
      necromancy: 'intelligence'
    },
    social: {
      persuasion: 'charisma',
      intimidation: 'charisma',
      deception: 'charisma',
      diplomacy: 'wisdom',
      trading: 'intelligence'
    },
    survival: {
      tracking: 'wisdom',
      trapping: 'dexterity',
      foraging: 'wisdom',
      navigation: 'intelligence',
      stealth: 'dexterity'
    },
    knowledge: {
      history: 'intelligence',
      arcana: 'intelligence',
      nature: 'intelligence',
      religion: 'wisdom',
      engineering: 'intelligence'
    },
    crafting: {
      alchemy: 'intelligence',
      smithing: 'strength',
      woodworking: 'dexterity',
      leatherworking: 'dexterity',
      cooking: 'constitution'
    }
  };

  return attributeMap[category]?.[skillName] || 'intelligence'; // Default to intelligence
}

/**
 * Create the final character object
 * @param {Object} characterData - Complete character data
 * @returns {Object} Processed character ready for saving
 */
export function finalizeCharacter(characterData) {
  const derivedStats = calculateDerivedStats(characterData);

  // Apply background bonuses
  let finalCharacter = characterData;
  if (characterData.background) {
    finalCharacter = applyBackgroundBonuses(characterData, characterData.background);
  }

  // Recalculate with background bonuses applied
  const finalStats = calculateDerivedStats(finalCharacter);

  return {
    ...finalCharacter,
    ...finalStats,
    created: new Date().toISOString(),
    version: '1.0',
    isComplete: true
  };
}

export default {
  CREATION_STEPS,
  INITIAL_CHARACTER_STATE,
  generateAttributes,
  calculateDerivedStats,
  generateCharacterName,
  validateCreationStep,
  getStepOptions,
  finalizeCharacter,
  getSkillAttribute,
  getSkillUpgradeCost,
  calculateSpentSkillPoints
};